/**
 * Account Abstraction (ERC-4337) API Helper
 * Communicates with backend AA endpoints for smart account management
 */
import { API } from '@/lib/backendUrl';

export const aaApi = {
  /** Get AA infrastructure configuration */
  getConfig: async () => {
    const res = await fetch(`${API}/api/aa/config`);
    if (!res.ok) throw new Error('Failed to fetch AA config');
    return res.json();
  },

  /** Get AA dashboard statistics */
  getDashboard: async () => {
    const res = await fetch(`${API}/api/aa/dashboard`);
    if (!res.ok) throw new Error('Failed to fetch AA dashboard');
    return res.json();
  },

  /** Create or retrieve a smart account for an EOA */
  createSmartAccount: async (eoaAddress, guestEmail = null, salt = 0) => {
    const res = await fetch(`${API}/api/aa/smart-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eoa_address: eoaAddress,
        guest_email: guestEmail,
        salt,
      }),
    });
    if (!res.ok) throw new Error('Failed to create smart account');
    return res.json();
  },

  /** Get smart account details for an EOA */
  getSmartAccount: async (eoaAddress) => {
    const res = await fetch(`${API}/api/aa/smart-account/${eoaAddress}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Failed to fetch smart account');
    return res.json();
  },

  /** List all registered smart accounts */
  listAccounts: async (skip = 0, limit = 50) => {
    const res = await fetch(`${API}/api/aa/accounts?skip=${skip}&limit=${limit}`);
    if (!res.ok) throw new Error('Failed to list smart accounts');
    return res.json();
  },

  /** Build an unsigned UserOperation */
  buildUserOp: async (eoaAddress, to, value = 0, callData = '0x') => {
    const res = await fetch(`${API}/api/aa/build-user-op`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eoa_address: eoaAddress,
        to,
        value,
        call_data: callData,
      }),
    });
    if (!res.ok) throw new Error('Failed to build UserOperation');
    return res.json();
  },

  /** Submit a signed UserOperation */
  submitUserOp: async (userOperation) => {
    const res = await fetch(`${API}/api/aa/submit-user-op`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_operation: userOperation }),
    });
    if (!res.ok) throw new Error('Failed to submit UserOperation');
    return res.json();
  },

  /** Get UserOperation receipt */
  getUserOpReceipt: async (userOpHash) => {
    const res = await fetch(`${API}/api/aa/user-op/${userOpHash}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Failed to fetch UserOp receipt');
    return res.json();
  },

  /** Get paymaster status */
  getPaymasterStatus: async () => {
    const res = await fetch(`${API}/api/aa/paymaster/status`);
    if (!res.ok) throw new Error('Failed to fetch paymaster status');
    return res.json();
  },

  /** Create a gasless booking (Phase 2) */
  createGaslessBooking: async (bookingData) => {
    const res = await fetch(`${API}/api/aa/gasless-booking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    });
    if (!res.ok) throw new Error('Failed to create gasless booking');
    return res.json();
  },

  /** Confirm a gasless booking after signing */
  confirmGaslessBooking: async (bookingId, signedUserOp = null) => {
    const res = await fetch(`${API}/api/aa/confirm-gasless-booking`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        booking_id: bookingId,
        signed_user_op: signedUserOp,
      }),
    });
    if (!res.ok) throw new Error('Failed to confirm gasless booking');
    return res.json();
  },

  /** Get gasless booking status */
  getGaslessBooking: async (bookingId) => {
    const res = await fetch(`${API}/api/aa/gasless-booking/${bookingId}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Failed to fetch gasless booking');
    return res.json();
  },

  // Phase 3: Session Keys

  /** Grant a session key */
  grantSessionKey: async (data) => {
    const res = await fetch(`${API}/api/aa/session-keys`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to grant session key');
    return res.json();
  },

  /** List session keys for a smart account */
  listSessionKeysByAccount: async (smartAccount) => {
    const res = await fetch(`${API}/api/aa/session-keys/by-account/${smartAccount}`);
    if (!res.ok) throw new Error('Failed to list session keys');
    return res.json();
  },

  /** List session keys for a booking */
  listSessionKeysByBooking: async (bookingId) => {
    const res = await fetch(`${API}/api/aa/session-keys/by-booking/${bookingId}`);
    if (!res.ok) throw new Error('Failed to list session keys');
    return res.json();
  },

  /** Get session key details */
  getSessionKey: async (keyId) => {
    const res = await fetch(`${API}/api/aa/session-keys/${keyId}`);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Failed to fetch session key');
    return res.json();
  },

  /** Revoke a session key */
  revokeSessionKey: async (keyId, eoaAddress) => {
    const res = await fetch(`${API}/api/aa/session-keys/${keyId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eoa_address: eoaAddress }),
    });
    if (!res.ok) throw new Error('Failed to revoke session key');
    return res.json();
  },

  /** Get session key statistics */
  getSessionKeyStats: async () => {
    const res = await fetch(`${API}/api/aa/session-keys/stats`);
    if (!res.ok) throw new Error('Failed to fetch session key stats');
    return res.json();
  },

  /** Trigger auto-checkout NFT conversion */
  autoCheckoutNft: async (bookingId) => {
    const res = await fetch(`${API}/api/aa/session-keys/auto-checkout/${bookingId}`, {
      method: 'POST',
    });
    if (!res.ok) throw new Error('Failed to trigger auto-checkout');
    return res.json();
  },

  // Phase 4: Social Recovery & Spending Limits

  /** Add a guardian */
  addGuardian: async (data) => {
    const res = await fetch(`${API}/api/aa/security/guardians`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to add guardian');
    return res.json();
  },

  /** List guardians for a smart account */
  listGuardians: async (smartAccount) => {
    const res = await fetch(`${API}/api/aa/security/guardians/${smartAccount}`);
    if (!res.ok) throw new Error('Failed to list guardians');
    return res.json();
  },

  /** Remove a guardian */
  removeGuardian: async (guardianId, eoaAddress) => {
    const res = await fetch(`${API}/api/aa/security/guardians/${guardianId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eoa_address: eoaAddress }),
    });
    if (!res.ok) throw new Error('Failed to remove guardian');
    return res.json();
  },

  /** Get recovery configuration */
  getRecoveryConfig: async (smartAccount) => {
    const res = await fetch(`${API}/api/aa/security/recovery-config/${smartAccount}`);
    if (!res.ok) throw new Error('Failed to get recovery config');
    return res.json();
  },

  /** Set spending limits */
  setSpendingLimits: async (data) => {
    const res = await fetch(`${API}/api/aa/security/spending-limits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to set spending limits');
    return res.json();
  },

  /** Get spending limits and usage */
  getSpendingLimits: async (smartAccount) => {
    const res = await fetch(`${API}/api/aa/security/spending-limits/${smartAccount}`);
    if (!res.ok) throw new Error('Failed to get spending limits');
    return res.json();
  },

  /** Get security stats */
  getSecurityStats: async () => {
    const res = await fetch(`${API}/api/aa/security/stats`);
    if (!res.ok) throw new Error('Failed to get security stats');
    return res.json();
  },
};
